import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch';
import { CH_HOME_FEATURE, VALID_CATEGORIES } from '@propertypal/shared/src/constants/content-hub';
import axios, { STRAPI_URL } from '@propertypal/shared/src/services/axios.strapi';
import purifyHtml from '@propertypal/shared/src/services/dompurify';
import { ContentHubFuzzySearch, ContentHubSearchItem } from '@propertypal/shared/src/types/strapi/fuzzy';
import { getArticleCategoryParams } from '@propertypal/shared/src/utils/content-hub';
import highlightTokens from '@propertypal/shared/src/utils/faq/highlightTokens';
import getArticleCategory from '@propertypal/shared/src/utils/getArticleCategory';
import platform from '@propertypal/shared/src/utils/platform';
import puralise from '@propertypal/shared/src/utils/puralise';
import React, { useEffect, useState } from 'react';
import Loader from 'react-spinners/PulseLoader';
import { useTheme } from 'styled-components';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import { MidHeading, Text } from '../typography';
import ContentHubBreadcrumbs from './ContentHubBreadcrumbs';
import {
  ContentHubSearchContainer,
  SearchResultsContainer,
  ContentHubSearchWrapper,
  LoadingBox,
  SearchNoResults,
  SearchLink,
  SearchContent,
  TotalResults,
  SearchResult,
} from './ContentHubSearchResults.style';

interface ContentHubSearchResultsProps {
  searchQuery: string;
  homeLink: string;
}

// Extract the first 200 characters from the HTML string
const truncatedBlurb = (content: string | null, tokens: string[]) => {
  if (content) {
    const truncated = content.replace(`\u00A0`, '').slice(0, 200);
    return `<span>${highlightTokens(truncated, tokens)}${content.length > truncated.length ? '...' : ''}</span>`;
  }
  return '';
};

const ContentHubSearchResults: React.FC<ContentHubSearchResultsProps> = ({ searchQuery, homeLink }) => {
  const theme = useTheme();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [articles, setArticles] = useState<ContentHubSearchItem[]>([]);
  const [tokens, setTokens] = useState<string[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);

  const fetchResult = async () => {
    setLoading(true);

    const { searchParams } = getArticleCategoryParams();

    const platformFilter: Record<string, string> = {
      'filters[articles][$or][0][platform][$eq]': platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
      'filters[articles][$or][1][platform][$eq]': 'Both',
      'filters[articles][$or][3]subCategories][platform][$eq]':
        platform.title === 'Propertynews' ? 'Propertynews' : 'PropertyPal',
      'filters[articles][$or][4][subCategories][platform][$eq]': 'Both',
    };

    if (platform.title === 'PropertyPal') {
      platformFilter['filters[articles][$or][2][platform][$null]'] = 'true';
      platformFilter['filters[articles][$or][5][subCategories][platform][$null]'] = 'true';
    }

    try {
      const result = await axios<ContentHubFuzzySearch>({
        method: 'GET',
        url: `${STRAPI_URL}/fuzzy-search/search`,
        params: {
          query: searchQuery,
          'filters[contentTypes]': 'articles',
          // filter out any categories hidden by flags
          ...searchParams,
          'filters[articles][$and][1][$or][0][hidden][$eq]': 'false',
          'filters[articles][$and][1][$or][1][hidden][$null]': 'true',
          'filters[articles][$and][2][publishedAt][$notNull]': 'true',
          ...platformFilter,
          'populate[articles][content][populate][0]': 'true',
          'populate[articles][subCategories][populate][1]': 'true',
          'pagination[articles][withcount]': 'true',
        },
      });

      setArticles(result.data?.articles?.data);
      setTotalResults(result.data?.articles?.meta.pagination?.total || 0);
      setError(false);
    } catch (err: any) {
      setError(true);
      setArticles([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  };

  // tokenise search query
  useEffect(() => {
    setTokens(searchQuery.split(/\s+/));
  }, [searchQuery]);

  // fetch articles that match search query
  useEffect(() => {
    fetchResult();
  }, [JSON.stringify(tokens)]);

  return (
    <ContentHubSearchContainer data-testid="article-search-results-container">
      <ContentHubSearchWrapper>
        <SearchResultsContainer>
          {loading && (
            <LoadingBox data-testid="article-search-loading">
              <Loader color={theme.primary} size={20} speedMultiplier={0.6} margin={8} />
            </LoadingBox>
          )}
          {!loading && articles?.length > 0 && (
            <div data-testid="article-search-results">
              <TotalResults fontSize={30} mb={55} data-testid="article-total-results">
                {totalResults} {puralise('result', 'results', totalResults)} for &quot;{searchQuery}&quot;
              </TotalResults>
              {articles?.map((article) => {
                // Only get valid sub category, not to list category not on platform, or not active
                const subCategory = article.subCategories?.find((s) => {
                  const articleCategory = s.articleCategory;
                  if (!articleCategory) return false;
                  return VALID_CATEGORIES.includes(articleCategory);
                });
                const categoryUrl = platform.select(`/${subCategory?.articleCategory}`, '');
                const subCategoryUrl = `${categoryUrl}/${subCategory?.slug}`;
                return (
                  <SearchResult key={article.id} data-testid="article-search-result">
                    <SearchLink
                      prefetch={false}
                      // eslint-disable-next-line max-len
                      href={`${subCategoryUrl}/${article.slug}`}
                      mb={10}
                      display="inline-block"
                      fontWeight="bold"
                      fontSize={18}
                      dangerouslySetInnerHTML={{ __html: purifyHtml(highlightTokens(article.title, tokens)) }}
                      data-testid="article-search-result-link"
                    />
                    <ContentHubBreadcrumbs
                      breadcrumbs={[
                        ...platform.select(
                          [
                            CH_HOME_FEATURE ? { title: 'Home', url: homeLink } : undefined,
                            {
                              title: getArticleCategory(subCategory?.articleCategory),
                              url: categoryUrl,
                            },
                            {
                              title: subCategory?.subCategory || '',
                              url: subCategoryUrl,
                            },
                          ],
                          [{ title: 'Home', url: homeLink }],
                        ),
                        {
                          title: (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: purifyHtml(highlightTokens(article.title, tokens)),
                              }}
                            />
                          ),
                        },
                      ]}
                      searchItem
                    />
                    <SearchContent html={truncatedBlurb(article.blurb, tokens)} />
                  </SearchResult>
                );
              })}
            </div>
          )}
          {!loading && !articles?.length && !error && (
            <SearchNoResults data-testid="articles-search-no-results">
              <FontAwesomeIcon icon={faSearch} size={40} />
              <MidHeading fontSize={40} mb={10} mt={10}>
                No Results
              </MidHeading>
              <Text fontSize={18}>Try changing your search criteria.</Text>
            </SearchNoResults>
          )}
          {error && (
            <SearchNoResults data-testid="article-search-error">
              <MidHeading fontSize={40} mb={10} mt={10}>
                Error
              </MidHeading>
              <Text fontSize={18}>
                Oops, something has went wrong during your search, contact {platform.helpEmail}.
              </Text>
            </SearchNoResults>
          )}
        </SearchResultsContainer>
      </ContentHubSearchWrapper>
    </ContentHubSearchContainer>
  );
};

export default ContentHubSearchResults;
